import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { PatientLabResultControllerApi } from "../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getXtenantId,
  useBaseContent,
} from "../../../utils/utils";

export interface LatestReport {
  uuid: string;
  orderUuid?: string;
  reportName: string;
  status: "Normal" | "Abnormal" | string;
  orderType?: "LAB" | "IMAGING" | string;
  recordedDate?: string;
  abnormalFlag?: string | null;
  resultValue?: string;
  createdByName?: string;
  labDocument?: string;
  fileName?: string;
}

interface UseLatestReportsProps {
  patientUuid: string;
  pagination: DGT_Pagination;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  enabled?: boolean;
}

/**
 * Fetch latest patient lab/imaging reports for the Patient Portal Dashboard
 * "Latest Reports" card. Returns the logged-in patient's active, non-archived
 * results sorted by `recordedDate` DESC by default.
 *
 * @see ECRH-4397
 */
export function useLatestReports(
  props: UseLatestReportsProps
): useBaseContent<LatestReport> {
  const prevPage = useRef<number>(props.pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination: props.pagination,
    prevPage,
  });
  const pageSize = props.pagination.pageSize;

  return useQuery({
    queryKey: ["latest-reports", pageSize, pageIndex, props],
    enabled: !!props.patientUuid && props.enabled !== false,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      return new PatientLabResultControllerApi(getConfigObject())
        .getLatestReports(
          props.patientUuid,
          pageIndex,
          pageSize,
          props.sortBy,
          props.sortDirection,
          getXtenantId()
        )
        .then((res) => res?.data as any);
    },
  });
}
